import { AlertTriangle, Clock, Plus, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import { Checkbox } from '@/shared/components/ui/checkbox'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import { Textarea } from '@/shared/components/ui/textarea'
import { cn } from '@/shared/lib/utils'
import type {
  ClaimChecklistItem,
  ClaimPhotoCategory,
} from '@/admin/hooks/use-lab-tasks'
import {
  newCustomItemId,
  type CustomRequestItem,
} from '@/admin/components/claims/document-request-value'

/**
 * The one checklist-request picker (WPS-130).
 *
 * Both surfaces that ask a claimant for evidence render this: the Lab
 * "Request Additional Photos" dialog (`lab-request-dialog.tsx`) and the intake
 * "Request More Documents" action widget (`document-request-input.tsx`). It was
 * extracted so there is a single implementation of the checklist checkboxes,
 * the "something else" ad-hoc rows, and the customer note - previously the two
 * surfaces carried near-identical copies.
 *
 * Presentational and fully controlled: the caller owns the checklist fetch,
 * the pre-tick/seeding policy (which differs per surface), the selection state
 * shape, and the submit path. Every section is opt-in via props, so a surface
 * only renders what its backend can actually consume - the ad-hoc "something
 * else" rows are shown only where the backend understands them (intake), never
 * on the Lab path whose request payload has no slot for them.
 *
 * Nothing here is tenant-specific: labels, descriptions and the option list all
 * come from the claim checklist API.
 */
export interface ChecklistRequestFieldsProps {
  /** Document checklist items (``kind === 'document'``). */
  documents: ClaimChecklistItem[]
  /** Photo categories (the tenant-wide catalogue). */
  photos: ClaimPhotoCategory[]
  isPending: boolean
  isError: boolean
  /** Currently-ticked checklist keys. */
  selectedKeys: Set<string>
  onToggleKey: (key: string) => void
  /**
   * Documents the request adds on its own (optional for the customer). They
   * show ticked and locked, and count as selected, but the backend adds them,
   * so they never enter ``selectedKeys``.
   */
  autoIncludedKeys?: ReadonlySet<string>
  /**
   * Section header copy. Defaults match the intake widget ("Documents" /
   * "Photos"); the Lab dialog passes "Photo categories" to keep its existing
   * wording. Caller concern, so each surface keeps the copy it shipped.
   */
  documentsLabel?: string
  photosLabel?: string
  /**
   * Ad-hoc "something else" rows. Omit both props to hide the section entirely
   * (the Lab path does this - its request payload cannot carry free-text asks).
   */
  customItems?: CustomRequestItem[]
  onCustomItemsChange?: (items: CustomRequestItem[]) => void
  /** Customer-visible note. Omit both props to hide the note field. */
  note?: string
  onNoteChange?: (note: string) => void
  /**
   * When true, warn (never disable) if nothing at all is selected - sending an
   * empty request tells the claimant nothing, which is the very bug this widget
   * exists to fix.
   */
  warnWhenEmpty?: boolean
}

export function ChecklistRequestFields({
  documents,
  photos,
  isPending,
  isError,
  selectedKeys,
  onToggleKey,
  autoIncludedKeys,
  documentsLabel,
  photosLabel,
  customItems,
  onCustomItemsChange,
  note,
  onNoteChange,
  warnWhenEmpty = false,
}: ChecklistRequestFieldsProps) {
  const { t } = useTranslation()

  const showCustom = customItems !== undefined && onCustomItemsChange !== undefined
  const showNote = note !== undefined && onNoteChange !== undefined

  const labelledCustomCount = (customItems ?? []).filter((i) =>
    i.label.trim(),
  ).length
  const autoCount = documents.filter((d) => autoIncludedKeys?.has(d.key)).length
  const selectedCount = selectedKeys.size + labelledCustomCount + autoCount

  const addCustom = () =>
    onCustomItemsChange?.([
      ...(customItems ?? []),
      { label: '', description: '', id: newCustomItemId() },
    ])

  const updateCustom = (
    index: number,
    patch: Partial<{ label: string; description: string }>,
  ) =>
    onCustomItemsChange?.(
      (customItems ?? []).map((item, i) =>
        i === index ? { ...item, ...patch } : item,
      ),
    )

  const removeCustom = (index: number) =>
    onCustomItemsChange?.((customItems ?? []).filter((_, i) => i !== index))

  const row = (
    key: string,
    label: string,
    satisfied: boolean,
    description?: string,
  ) => {
    const auto = autoIncludedKeys?.has(key) ?? false
    return (
      <Label
        key={key}
        className={cn(
          'flex items-start gap-2.5 rounded-[4px] px-1.5 py-1.5',
          auto ? 'cursor-default' : 'cursor-pointer hover:bg-[var(--bg-light)]',
        )}
      >
        <Checkbox
          checked={auto || selectedKeys.has(key)}
          disabled={auto}
          onCheckedChange={() => onToggleKey(key)}
          className="mt-0.5"
        />
        <span className="flex flex-1 flex-col gap-0.5">
          <span className="text-[12px] text-[var(--ink)]">{label}</span>
          {auto && (
            <span className="text-[11px] text-[var(--ink-3)]">
              {t(
                'claims.documentRequest.autoIncluded',
                'Added automatically - optional for the customer',
              )}
            </span>
          )}
          {description && (
            <span className="text-[11px] text-[var(--ink-3)]">{description}</span>
          )}
        </span>
        {satisfied && (
          <Badge variant="secondary" className="shrink-0 text-[9px]">
            {t('claims.documentRequest.received', 'Received')}
          </Badge>
        )}
      </Label>
    )
  }

  if (isPending) {
    return (
      <div className="flex items-center gap-2 py-2 text-[12px] text-[var(--ink-3)]">
        <Clock className="h-3.5 w-3.5 shrink-0 animate-spin" />
        {t('claims.loadingChecklist', 'Loading checklist...')}
      </div>
    )
  }
  if (isError) {
    return (
      <div className="flex items-start gap-2 rounded-[8px] border-[0.5px] border-[var(--status-error)] bg-[var(--status-error-bg)] px-3.5 py-2.5">
        <AlertTriangle className="mt-[1px] h-3.5 w-3.5 shrink-0 text-[var(--status-error)]" />
        <p className="text-[12px] text-[var(--ink)]">
          {t(
            'claims.errorLoadingChecklist',
            'Could not load the checklist for this claim. Close this dialog and try again.',
          )}
        </p>
      </div>
    )
  }

  // A surface with no checklist AND no ad-hoc escape hatch has nothing to
  // offer - guide the user to set the defect so a checklist can be prepared.
  if (documents.length === 0 && photos.length === 0 && !showCustom) {
    return (
      <div className="flex items-start gap-2 rounded-[8px] border-[0.5px] border-[var(--status-warn)] bg-[var(--status-warn-bg)] px-3.5 py-2.5">
        <AlertTriangle className="mt-[1px] h-3.5 w-3.5 shrink-0 text-[var(--status-warn)]" />
        <div className="flex flex-col gap-0.5">
          <p className="text-[12px] font-medium text-[var(--ink)]">
            {t('claims.noChecklistAvailable', 'No checklist available yet')}
          </p>
          <p className="text-[12px] text-[var(--ink-3)]">
            {t(
              'claims.noChecklistDescription',
              "Set the claim's major/minor defect so the right photo and document checklist can be prepared, then reopen this dialog.",
            )}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-baseline justify-between">
        <span className="text-[11px] font-medium uppercase tracking-[0.08em] text-[var(--ink-2)]">
          {t('claims.documentRequest.title', 'What to request')}
        </span>
        <span className="text-[11px] text-[var(--ink-3)]">
          {t('claims.documentRequest.count', '{{count}} selected', {
            count: selectedCount,
          })}
        </span>
      </div>

      {warnWhenEmpty && selectedCount === 0 && (
        <p className="rounded-[4px] border-[0.5px] border-[var(--status-warn)] bg-[var(--status-warn-bg)] px-3 py-2 text-[11px] text-[var(--ink-2)]">
          {t(
            'claims.documentRequest.nothingSelected',
            'Nothing is selected, so the claimant will not be told what to send. Tick an item or add one below.',
          )}
        </p>
      )}

      <div className="flex max-h-[38vh] flex-col gap-3 overflow-y-auto">
        {documents.length > 0 && (
          <div className="flex flex-col gap-1">
            <span className="text-[10px] uppercase tracking-[0.1em] text-[var(--ink-3)]">
              {documentsLabel ?? t('claims.documentRequest.documents', 'Documents')}
            </span>
            {documents.map((d) => row(d.key, d.label, d.satisfied, d.description))}
          </div>
        )}

        {photos.length > 0 && (
          <div className="flex flex-col gap-1">
            <span className="text-[10px] uppercase tracking-[0.1em] text-[var(--ink-3)]">
              {photosLabel ?? t('claims.documentRequest.photos', 'Photos')}
            </span>
            {photos.map((p) => row(p.key, p.label, p.satisfied, p.description))}
          </div>
        )}

        {showCustom && (
          <div className="flex flex-col gap-2">
            <span className="text-[10px] uppercase tracking-[0.1em] text-[var(--ink-3)]">
              {t('claims.documentRequest.other', 'Something else')}
            </span>
            {(customItems ?? []).map((item, index) => (
              <div key={item.id} className="flex items-start gap-2">
                <div className="flex flex-1 flex-col gap-1.5">
                  <Input
                    value={item.label}
                    placeholder={t(
                      'claims.documentRequest.customLabel',
                      'What do you need? e.g. Contractor invoice',
                    )}
                    onChange={(e) => updateCustom(index, { label: e.target.value })}
                  />
                  <Input
                    value={item.description}
                    placeholder={t(
                      'claims.documentRequest.customDescription',
                      'Optional detail shown to the claimant',
                    )}
                    onChange={(e) =>
                      updateCustom(index, { description: e.target.value })
                    }
                  />
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  onClick={() => removeCustom(index)}
                  aria-label={t('claims.documentRequest.remove', 'Remove item')}
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              size="xs"
              onClick={addCustom}
              className="self-start text-[11px]"
            >
              <Plus className="mr-1 h-3 w-3" />
              {t('claims.documentRequest.addItem', 'Add another item')}
            </Button>
          </div>
        )}
      </div>

      {showNote && (
        <div className="flex flex-col gap-1.5">
          <span className="text-[10px] uppercase tracking-[0.1em] text-[var(--ink-3)]">
            {t('claims.documentRequest.note', 'Note to the claimant')}
          </span>
          <Textarea
            value={note}
            rows={2}
            placeholder={t(
              'claims.documentRequest.notePlaceholder',
              'Optional - appears at the top of the email',
            )}
            onChange={(e) => onNoteChange?.(e.target.value)}
          />
        </div>
      )}
    </div>
  )
}
